import { Link as RouterLink } from 'react-router-dom'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Link from '@mui/material/Link'
import Typography from '@mui/material/Typography'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {
  isAssistantToolError,
  type AssistantActionOutput,
  type AssistantMyCasesOutput,
  type AssistantSuggestRepliesOutput,
  type AssistantToolError,
  type AssistantToolOutputs,
} from '../contract'
import { PATHS_WITH_SECTIONS } from '../../../router/RoutesUtils'
import { assistantCasePath } from './AssistantCaseCard'
import AssistantCaseList from './AssistantCaseList'
import AssistantCaseSummary from './AssistantCaseSummary'
import AssistantReferralDraftCard from './AssistantReferralDraftCard'
import HelpArticlesCard from './HelpArticlesCard'

type AnyToolOutput = AssistantToolOutputs[keyof AssistantToolOutputs]

const OpenCaseLink = ({ caseId }: { caseId: string }) => (
  <Link
    component={RouterLink}
    to={assistantCasePath(caseId)}
    variant="caption"
    underline="hover"
  >
    Open case
  </Link>
)

const MY_CASES_SECTIONS = new Set(['outbound', 'inbound'])

const OpenMyCasesLink = ({ side }: { side: string }) =>
  MY_CASES_SECTIONS.has(side) ? (
    <Link
      component={RouterLink}
      to={`${PATHS_WITH_SECTIONS.MY_REFERRALS}/${side}`}
      variant="caption"
      underline="hover"
    >
      Go to My Cases
    </Link>
  ) : null

/** A change that failed or could not run, in the server's own words. */
const ToolErrorNote = ({ error }: { error: AssistantToolError }) => (
  <Box
    data-testid="assistant-tool-error"
    role="status"
    sx={{
      display: 'flex',
      gap: 1,
      p: 1.5,
      borderRadius: 1,
      bgcolor: 'action.hover',
    }}
  >
    <InfoOutlinedIcon fontSize="small" color="action" />
    <Box>
      <Typography variant="body2">{error.message}</Typography>
      {error.caseId && <OpenCaseLink caseId={error.caseId} />}
    </Box>
  </Box>
)

/** A completed change: what happened, written by the server. */
const ActionDone = ({ output }: { output: AssistantActionOutput }) => {
  const caseId = output.newCaseId ?? output.caseId
  return (
    <Box
      data-testid="assistant-action-result"
      sx={{
        display: 'flex',
        gap: 1,
        p: 1.5,
        borderRadius: 1,
        border: 1,
        borderColor: 'success.light',
        bgcolor: 'background.paper',
      }}
    >
      <CheckCircleOutlineIcon fontSize="small" color="success" />
      <Box>
        <Typography variant="body2">{output.headline}</Typography>
        {output.detail && (
          <Typography variant="caption" color="text.secondary" component="p">
            {output.detail}
          </Typography>
        )}
        {caseId && <OpenCaseLink caseId={caseId} />}
        {!caseId && output.openMyCases && (
          <OpenMyCasesLink side={output.openMyCases} />
        )}
      </Box>
    </Box>
  )
}

const humanize = (key: string) => {
  const words = key.replace(/[_-]+/g, ' ').trim()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

const MY_CASES_HEADINGS: Record<AssistantMyCasesOutput['side'], string> = {
  outbound: 'Cases you referred',
  inbound: 'Cases referred to you',
}

const MyCases = ({ output }: { output: AssistantMyCasesOutput }) => {
  if (output.tab === 'counts' && output.counts) {
    return (
      <Box data-testid="assistant-case-counts">
        <Typography variant="overline" color="text.secondary">
          {MY_CASES_HEADINGS[output.side]}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {Object.entries(output.counts).map(([key, count]) => (
            <Chip
              key={key}
              size="small"
              variant="outlined"
              label={`${humanize(key)}: ${count}`}
            />
          ))}
        </Box>
      </Box>
    )
  }
  return (
    <AssistantCaseList
      cases={output.cases}
      heading={MY_CASES_HEADINGS[output.side]}
      emptyText="No cases here right now."
    />
  )
}

const SuggestedReplies = ({
  output,
  onReply,
  disabled,
}: {
  output: AssistantSuggestRepliesOutput
  onReply?: (text: string) => void
  disabled?: boolean
}) => {
  if (!onReply || output.options.length === 0) return null
  return (
    <Box data-testid="assistant-suggested-replies">
      {output.question && (
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          {output.question}
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {output.options.map(option => (
          <Chip
            key={option}
            label={option}
            color="primary"
            variant="outlined"
            disabled={disabled}
            onClick={() => onReply(option)}
          />
        ))}
      </Box>
    </Box>
  )
}

interface AssistantToolResultProps {
  output: unknown
  /** Sends a quick reply; only the latest message offers them. */
  onReply?: (text: string) => void
  disabled?: boolean
}

/**
 * Renders a finished action's structured result as an on-brand card. Results
 * the model only uses to reason (option lists) render nothing.
 */
const AssistantToolResult = ({
  output,
  onReply,
  disabled,
}: AssistantToolResultProps) => {
  if (isAssistantToolError(output)) return <ToolErrorNote error={output} />
  if (!output || typeof output !== 'object' || !('kind' in output)) return null

  const result = output as Exclude<AnyToolOutput, AssistantToolError>
  switch (result.kind) {
    case 'cases':
      return (
        <AssistantCaseList
          cases={result.cases}
          heading={`${result.total} case${result.total === 1 ? '' : 's'} found`}
          emptyText="No cases matched. Try a broader search."
        />
      )
    case 'my_cases':
      return <MyCases output={result} />
    case 'case':
      return <AssistantCaseSummary data={result.case} />
    case 'needs_attention':
      if (result.outbound.count + result.inbound.count === 0) {
        return (
          <Typography variant="body2" color="text.secondary">
            Nothing needs your attention right now.
          </Typography>
        )
      }
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <AssistantCaseList
            cases={result.outbound.cases}
            heading={`${MY_CASES_HEADINGS.outbound} (${result.outbound.count})`}
          />
          <AssistantCaseList
            cases={result.inbound.cases}
            heading={`${MY_CASES_HEADINGS.inbound} (${result.inbound.count})`}
          />
        </Box>
      )
    case 'referral_draft':
      return (
        <AssistantReferralDraftCard
          draft={result.draft}
          missing={result.missing}
        />
      )
    case 'help_articles':
      return <HelpArticlesCard output={result} />
    case 'action':
      return <ActionDone output={result} />
    case 'suggest_replies':
      return (
        <SuggestedReplies
          output={result}
          onReply={onReply}
          disabled={disabled}
        />
      )
    default:
      return null
  }
}

export default AssistantToolResult
